import {
  GridColDef,
  GridColumnMenuProps,
  GridColumnMenuItemProps,
  GridCellParams,
  GridRowModel,
  GridApiPremium,
  GridCellEditCommitParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium';
import { OTCResponse, PriceStatus } from '../../types/generatedTypes';
import { Group } from '../../components/Groups/types';
import { ViewOperations, OTCView } from '../../hooks/useViewManagement/types';

// Row type - extends OTCResponse with required id field
export type OTCRow = OTCResponse & { id: string };

// Constants for column fields
export const DAYS_SINCE_ILLIO_UPDATE = 'daysSinceIllioUpdate';

// Column confirmation modal types
export interface DeleteColumnConfirmation {
  open: boolean;
  columnField: string;
  columnName: string;
}

// Extended column menu props
export interface ExtendedGridColumnMenuProps extends GridColumnMenuProps {
  onDeleteColumn?: (field: string) => void;
  onDeleteColumnClick?: (field: string, name: string) => void;
}

// Delete column menu item props
export interface DeleteColumnMenuItemProps extends GridColumnMenuItemProps {
  onDelete: () => void;
}

// Cell status maps
export type CellStatusMap = Record<string, Record<string, PriceStatus>>;
export type UpdatedCellsMap = Record<string, Set<string>>;
export type IllioValuesMap = Record<
  string,
  Record<string, string | number | null>
>;

// Comparison difference entry from Illio
export interface ComparisonDiff {
  field: string;
  status: PriceStatus;
  illioValue: string | number | null;
}

// Helper function types
export type GetRowIdFn = (row: OTCRow) => string;
export type CalculatePnLFn = (
  row: OTCRow,
  dateFields: string[]
) => number | null;
export type CalculateDaysSinceIllioUpdateFn = (
  row: OTCRow,
  rowId: string,
  updatedCells: UpdatedCellsMap
) => number | null;

// Hook prop interfaces

// useOTCData props and return types
export interface UseOTCDataProps {
  apiRef: React.RefObject<GridApiPremium>;
  displayData: OTCRow[];
}

export interface UseOTCDataReturn {
  otcData: OTCRow[];
  originalData: Record<string, OTCRow>;
  loading: boolean;
  selectedRows: string[];
  lastSyncDate: string | null;
  showInactiveInstruments: boolean;
  setShowInactiveInstruments: React.Dispatch<React.SetStateAction<boolean>>;
  groups: Group[];
  setGroups: React.Dispatch<React.SetStateAction<Group[]>>;
  isGroupingEnabled: boolean;
  isGroupsModalOpen: boolean;
  availableRowsForGroups: Array<{
    id: string;
    label: string;
    [key: string]: any;
  }>;
  fetchOTCData: (includeInactive?: boolean) => Promise<void>;
  setOTCData: React.Dispatch<React.SetStateAction<OTCRow[]>>;
  setOriginalData: React.Dispatch<React.SetStateAction<Record<string, OTCRow>>>;
  handleRowSelectionChange: (newSelectionModel: GridRowSelectionModel) => void;
  setSelectedRows: React.Dispatch<React.SetStateAction<string[]>>;
  handleOpenGroupsModal: () => void;
  handleCloseGroupsModal: () => void;
  handleSaveGroups: (groups: Group[]) => void;
  setIsGroupingEnabled: (enabled: boolean) => void;
  loadGroupsFromBackend: () => Promise<void>;
  calculatePnL: CalculatePnLFn;
  calculateDaysSinceIllioUpdate: CalculateDaysSinceIllioUpdateFn;
}

// useOTCColumns props and return types
export interface UseOTCColumnsProps {
  otcData: OTCRow[];
  autosizeColumns: boolean;
  isGroupingEnabled: boolean;
  calculatePnL: CalculatePnLFn;
  apiRef: React.RefObject<GridApiPremium>;
  viewManager: ViewManager;
}

export interface UseOTCColumnsReturn {
  columns: GridColDef[];
  modalOpen: boolean;
  deleteColumnConfirmation: DeleteColumnConfirmation | null;
  hiddenColumnFields: Set<string>;
  setColumns: React.Dispatch<React.SetStateAction<GridColDef[]>>;
  setModalOpen: (open: boolean) => void;
  setHiddenColumnFields: React.Dispatch<React.SetStateAction<Set<string>>>;
  handleAddColumn: (name: string) => void;
  handleDeleteColumnClick: (field: string, name: string) => void;
  handleConfirmDeleteColumn: () => Promise<void>;
  handleColumnVisibilityChange: (model: Record<string, boolean>) => void;
  getCurrentDateColumns: () => string[];
  setDeleteColumnConfirmation: React.Dispatch<
    React.SetStateAction<DeleteColumnConfirmation | null>
  >;
}

// useOTCIllio props and return types
export interface UseOTCIllioProps {
  otcData: OTCRow[];
  originalData: Record<string, OTCRow>;
  selectedRows: string[];
  setOTCData: React.Dispatch<React.SetStateAction<OTCRow[]>>;
  setOriginalData: React.Dispatch<React.SetStateAction<Record<string, OTCRow>>>;
  setSelectedRows: React.Dispatch<React.SetStateAction<string[]>>;
  fetchOTCData: (includeInactive?: boolean) => Promise<void>;
  calculateDaysSinceIllioUpdate: CalculateDaysSinceIllioUpdateFn;
  showInactiveInstruments: boolean;
}

export interface UseOTCIllioReturn {
  updatedCells: UpdatedCellsMap;
  cellStatusMap: CellStatusMap;
  illioValuesMap: IllioValuesMap;
  illioCheckLoading: boolean;
  illioUploadLoading: boolean;
  syncLoading: boolean;
  showOnlyIllioDifferences: boolean;
  setShowOnlyIllioDifferences: (show: boolean) => void;
  showSyncConfirmation: boolean;
  setShowSyncConfirmation: (show: boolean) => void;
  checkAllIllioDifferences: () => Promise<void>;
  failedOtcIds: string[];
  retryFailedOtcCheck: () => Promise<void>;
  checkDifferenceWithIllioForRow: (
    row: OTCRow,
    updatedFields?: Set<string>
  ) => Promise<void>;
  handleIllioUpload: () => Promise<void>;
  handleSyncClick: () => void;
  confirmSyncFromIllio: () => Promise<void>;
  getCellClassName: (params: GridCellParams) => string;
  toggleIllioDifferencesFilter: () => void;
  clearUpdatedCellsForRows: (rowIds: string[]) => void;
  clearAllIllioComparisons: () => void;
  clearIndicatorForCell: (rowId: string, field: string) => void;
}

// useOTCOperations props and return types
export interface UseOTCOperationsProps {
  otcData: OTCRow[];
  originalData: Record<string, OTCRow>;
  setOTCData: React.Dispatch<React.SetStateAction<OTCRow[]>>;
  columns: GridColDef[];
  checkDifferenceWithIllioForRow: (
    row: OTCRow,
    updatedFields?: Set<string>
  ) => Promise<void>;
  calculatePnL: CalculatePnLFn;
  clearUpdatedCellsForRows: (rowIds: string[]) => void;
  setOriginalData: React.Dispatch<React.SetStateAction<Record<string, OTCRow>>>;
  fetchOTCData: (includeInactive?: boolean) => Promise<void>;
  selectedRows: string[];
  setSelectedRows: React.Dispatch<React.SetStateAction<string[]>>;
}

export interface UseOTCOperationsReturn {
  processRowUpdate: (newRow: GridRowModel) => GridRowModel;
  handleCellEditCommit: (params: GridCellEditCommitParams) => unknown;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  getUndoActionInfo: () => { description: string } | null;
  getRedoActionInfo: () => { description: string } | null;
}

// useOTCBackup props and return types
export interface UseOTCBackupProps {
  fetchOTCData: (includeInactive?: boolean) => Promise<void>;
  setOTCData: React.Dispatch<React.SetStateAction<OTCRow[]>>;
  clearAllIllioComparisons: () => void;
  setSelectedRows: React.Dispatch<React.SetStateAction<string[]>>;
  showInactiveInstruments: boolean;
}

export interface UseOTCBackupReturn {
  isDownloadingBackup: boolean;
  isRestoringBackup: boolean;
  handleDownloadBackup: () => Promise<void>;
  handleRestoreBackup: () => Promise<void>;
}

// useOTCFilters props and return types
// Type alias for the view manager
export type ViewManager = ViewOperations<OTCView> & { loading?: boolean };

export interface UseOTCFiltersProps {
  otcData: OTCRow[];
  searchFilteredData: OTCRow[];
  updatedCells: UpdatedCellsMap;
  columns: GridColDef[];
  hiddenColumnFields: Set<string>;
  apiRef: React.RefObject<GridApiPremium>;
  setHiddenColumnFields: React.Dispatch<React.SetStateAction<Set<string>>>;
  updateViewModifications: () => void;
  viewManager: ViewManager;
  isGroupingEnabled: boolean;
  showOnlyIllioDifferences: boolean;
  hideEmptyDateColumns: boolean;
  setHideEmptyDateColumns: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface UseOTCFiltersReturn {
  filteredData: OTCRow[];
  toggleHideEmptyDateColumns: () => void;
  applyHideEmptyDateColumns: (shouldHide: boolean) => void;
  handleExport: () => void;
}

// Utility helper types
export interface ValueComparisonResult {
  areEqual: boolean;
  hasChanges: boolean;
}

export interface RowUpdateValidation {
  isValid: boolean;
  sanitizedRow: GridRowModel;
  changedFields: Set<string>;
}

// Export helper function type
export { GetRowIdFn as getRowId };
